<script lang="ts" setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useIncidentSubmissionStore } from '../../stores/incident-submission'
import { formatDistance } from '../../utils/format'
import {
  crashSeverityLine,
  crashSourceAndDate,
  crashTypeAndAddress
} from '../../utils/recorded-crash-text'

// Optional step (RF-071): is the footage of one of the official crash records near the pin? A recorded crash is open data,
// not footage, so every option says so and uses the amber style of the staff CrashLinkPanel, never the blue footage pin.
const incidentStore = useIncidentSubmissionStore()
const { nearbyCrashes, selectedCrashId } = storeToRefs(incidentStore)

const NONE = 'none'
const choice = computed(() => selectedCrashId.value ?? NONE)

function onChoose(value: string) {
  incidentStore.selectCrash(value === NONE ? null : value)
}
</script>

<template>
  <div class="row q-col-gutter-md">
    <div class="col-12">
      <h6 class="text-h6 q-mt-none q-mb-xs text-blue-grey-9" tabindex="-1" data-step-heading>
        Is this one of these recorded crashes?
      </h6>
      <p class="text-body2 text-blue-grey-8" data-testid="recorded-crash-note">
        Official crash records near your pin, within a day of your time. Linking is optional and
        only shows publicly after our team approves it.
      </p>
    </div>

    <div class="col-12">
      <q-list class="q-gutter-y-sm">
        <q-item
          v-for="crash in nearbyCrashes"
          :key="crash.id"
          tag="label"
          class="bg-amber-1 text-grey-10 rounded-borders"
          data-testid="recorded-crash-option"
        >
          <q-item-section side top>
            <q-radio
              :model-value="choice"
              :val="crash.id"
              data-testid="recorded-crash-radio"
              @update:model-value="onChoose"
            />
          </q-item-section>
          <q-item-section avatar top>
            <q-icon name="mdi-alert-circle-outline" color="warning" />
          </q-item-section>
          <q-item-section>
            <q-item-label
              class="text-weight-bold text-uppercase"
              data-testid="recorded-crash-label"
            >
              Recorded crash, not footage
            </q-item-label>
            <q-item-label data-testid="recorded-crash-distance">
              {{ formatDistance(crash.distanceMeters) }} away
            </q-item-label>
            <q-item-label data-testid="recorded-crash-source-date">
              {{ crashSourceAndDate(crash) }}
            </q-item-label>
            <q-item-label data-testid="recorded-crash-severity">
              {{ crashSeverityLine(crash) }}
            </q-item-label>
            <q-item-label data-testid="recorded-crash-type-address">
              {{ crashTypeAndAddress(crash) }}
            </q-item-label>
          </q-item-section>
        </q-item>

        <q-item tag="label" data-testid="recorded-crash-none">
          <q-item-section side>
            <q-radio
              :model-value="choice"
              :val="NONE"
              data-testid="recorded-crash-none-radio"
              @update:model-value="onChoose"
            />
          </q-item-section>
          <q-item-section>None of these</q-item-section>
        </q-item>
      </q-list>
    </div>
  </div>
</template>
